import { Fragment, FunctionalComponent } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import {
  useSelector,
  useCheckoutContext,
} from '@primer-io/shared-library/contexts';
import { Icons } from '../../assets/icons/Icons';
import { CardNetwork } from '../../types';
import { AnimatePresence } from '../../utils/AnimatePresence';
import { usePresence } from '../../utils/AnimatePresence/use-presence';
import { delay } from '../../utils/delay';

const CardIconData = {
  'visa': {
    icon: Icons.visa,
    alt: 'Visa Logo',
  },
  'mastercard': {
    icon: Icons.mastercard,
    alt: 'Mastercard Logo',
  },
  'american-express': {
    icon: Icons.americanExpress,
    alt: 'American Express Logo',
  },
  'jcb': {
    icon: Icons.jcb,
    alt: 'JCB Logo',
  },
  'discover': {
    icon: Icons.discover,
    alt: 'Discover Logo',
  },
};

const defaultCardIconData = {
  icon: Icons.genericCard,
  alt: 'Card Logo',
};

const CardTypes = Object.keys(CardIconData) as Array<CardNetwork>;

type CardIconProps = {
  cardType?: string;
  isHidden?: boolean;
  showError?: boolean;
};

const CardIcon: FunctionalComponent<CardIconProps> = ({
  cardType = 'default',
  isHidden,
  showError = false,
}) => {
  const { icon, alt } = CardIconData[cardType] ?? defaultCardIconData;

  return (
    <div
      class={`PrimerCheckout__cardType ${
        isHidden ? 'PrimerCheckout--hidden' : ''
      } ${showError ? 'PrimerCheckout--error' : ''}`}
    >
      <img src={icon} alt={alt} />
    </div>
  );
};

///////////////////////////////////////////
// Cycle
///////////////////////////////////////////

const CYCLE_DURATION = 1500;

const CycleItem: FunctionalComponent = ({ children }) => {
  const [isPresent, safeToRemove] = usePresence();
  const [hidden, setHidden] = useState(true);

  useEffect(() => {
    setHidden(false);
  }, []);

  useEffect(() => {
    if (isPresent) {
      return;
    }

    (async () => {
      setHidden(true);
      await delay(500);
      safeToRemove?.();
    })();
  }, [isPresent]);

  return (
    <div
      style={{
        position: 'absolute',
        transition: 'opacity linear 500ms',
        opacity: hidden ? 0 : 1,
      }}
    >
      {children}
    </div>
  );
};

type CycleProps = {
  cardTypes: string[];
  isHidden?: boolean;
};

const Cycle: FunctionalComponent<CycleProps> = ({
  cardTypes,
  isHidden = false,
}) => {
  const [idx, setIdx] = useState(0);
  const idxRef = useRef(0);
  const cardTypesLengthRef = useRef(cardTypes.length);

  const incrementIdx = () => {
    const newIdx = (idxRef.current + 1) % cardTypesLengthRef.current;
    idxRef.current = newIdx;
    setIdx(newIdx);
  };

  useEffect(() => {
    cardTypesLengthRef.current = cardTypes.length;
  }, [cardTypes]);

  useEffect(() => {
    setIdx(0);
    const interval = setInterval(() => {
      incrementIdx();
    }, CYCLE_DURATION);

    return () => {
      clearInterval(interval);
    };
  }, []);

  const currentCardType = cardTypes[idx];

  return (
    <div
      class={`PrimerCheckout__cardType ${
        isHidden ? 'PrimerCheckout--hidden' : ''
      }`}
    >
      <AnimatePresence>
        {[
          <CycleItem key={currentCardType}>
            <CardIcon cardType={currentCardType} />
          </CycleItem>,
        ]}
      </AnimatePresence>
    </div>
  );
};

///////////////////////////////////////////
// CardIcons
///////////////////////////////////////////

const MIN_CARD_LENGTH_CHECK_TYPE = 2;

const CardIcons = () => {
  const { options } = useCheckoutContext();
  const allowedCardNetworks = options.allowedCardNetworks ?? CardTypes;

  const cardMeta = useSelector((s) => s.card.metadata);
  const currentCardType = cardMeta.type;
  const cardNumberLength = cardMeta.cardNumberLength ?? 0;

  const numberFieldError = useSelector((s) => s.card.numberField.error);
  const hasUnsupportedError = numberFieldError === 'unsupportedCardType';

  const cardTypes = CardTypes.filter((cardType) =>
    allowedCardNetworks.includes(cardType),
  );

  const staticCardTypes = cardTypes.slice(0, 2);
  const cycleCardTypes = cardTypes.slice(2);

  const contents: Array<FunctionalComponent> = [];

  // Appear
  contents.push(
    <CardIcon
      key='appear'
      cardType={currentCardType === null ? undefined : currentCardType}
      isHidden={
        cardNumberLength < MIN_CARD_LENGTH_CHECK_TYPE ||
        (currentCardType ? staticCardTypes.includes(currentCardType) : false)
      }
      showError={hasUnsupportedError}
    />,
  );

  // Static icons
  contents.push(
    ...staticCardTypes.map((cardType) => {
      return (
        <CardIcon
          key={cardType}
          cardType={cardType}
          isHidden={
            cardNumberLength >= MIN_CARD_LENGTH_CHECK_TYPE &&
            (currentCardType ? currentCardType !== cardType : true)
          }
        />
      );
    }),
  );

  // Cycle icons
  if (cycleCardTypes.length) {
    contents.push(
      <Cycle
        key='cycle'
        cardTypes={cycleCardTypes}
        isHidden={cardNumberLength >= MIN_CARD_LENGTH_CHECK_TYPE}
      />,
    );
  }

  return (
    <div id='primer-checkout-card-types' class='PrimerCheckout__cardTypes'>
      {contents}
    </div>
  );
};

export default CardIcons;
